<script lang="ts">
	/**
	 * Outbound delivery health across **every** application (Design §8.1, §13).
	 *
	 * `/Applications` answers the same question one application at a time, from that
	 * application's own page. The operational question is the opposite one — is anything dead
	 * anywhere — and answering it by visiting each application in turn is exactly how a
	 * dead-lettered integration stays unnoticed for a month.
	 *
	 * `dead` is the status that matters and it is not the end of the story: a dead letter enqueues
	 * a repair, and that repair is the row flagged *resync*. It retries slowly and indefinitely, so
	 * a dead row means "this customer is being repaired, slowly", not "this customer is abandoned".
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import {
		EmptyState,
		InfiniteLoadElement,
		Panel,
		RelativeDate,
		SelectField,
		WebhookResponseView
	} from '../../../components';
	import { listAllWebhookDeliveries, retryWebhookDelivery } from '../../../AppNetwork';
	import type { WebhookDelivery } from '../../../AppTypes';
	import { toastError, toastSuccess } from '../../../stores/toast';

	const PAGE_SIZE = 25;

	let deliveries: WebhookDelivery[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadError: string = '';
	let busyUUID: string = '';
	let statusFilter: string = '';
	let sentinel: InfiniteLoadElement | undefined;

	const statusOptions = [
		{ value: '', name: 'Any status', description: 'No narrowing by status.' },
		{ value: 'pending', name: 'Queued', description: 'Waiting for the delivery worker to send it.' },
		{ value: 'delivering', name: 'In flight', description: 'Sent and waiting for the application to answer; the lease returns it if the worker dies.' },
		{ value: 'delivered', name: 'Delivered', description: 'The application answered with a 2xx. Its reply is recorded on the row.' },
		{ value: 'failed', name: 'Retrying', description: 'The last attempt failed; another is scheduled on the retry ladder.' },
		{ value: 'dead', name: 'Dead letters', description: 'Every retry failed. Nothing more is attempted until someone retries or replays it.' }
	];

	const statusColors: Record<string, 'green' | 'blue' | 'yellow' | 'red' | 'dark'> = {
		pending: 'dark',
		delivering: 'blue',
		delivered: 'green',
		failed: 'yellow',
		dead: 'red'
	};

	async function fnLoad(reset: boolean = true): Promise<void> {
		if (reset) {
			loading = true;
			deliveries = [];
		}
		loadError = '';
		try {
			const page = await listAllWebhookDeliveries({
				status: statusFilter,
				limit: PAGE_SIZE,
				offset: reset ? 0 : deliveries.length
			});
			deliveries = reset ? page.items : [...deliveries, ...page.items];
			total = page.total;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnRetry(delivery: WebhookDelivery): Promise<void> {
		busyUUID = delivery.uuid;
		try {
			await retryWebhookDelivery(delivery.uuid);
			toastSuccess('Queued for the next poll.');
			await fnLoad();
		} catch (error) {
			toastError('The delivery was not queued.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnLoadMore(): Promise<void> {
		await fnLoad(false);
		sentinel?.resetVisibilityTracking();
		sentinel?.updateState();
	}

	$: hasMore = deliveries.length < total;

	onMount(() => fnLoad());
</script>

<Panel title="Webhook deliveries, store-wide" faIcon="paper-plane">
	<svelte:fragment slot="titleRight">
		<span class="text-default text-xs">{total} {total === 1 ? 'delivery' : 'deliveries'}</span>
	</svelte:fragment>

	<div class="space-y-4">
		<p class="text-default text-sm">
			Every application at once, because the per-application view only shows a broken integration
			to whoever thought to look at it. <strong>Dead letters</strong> are the status that matters:
			and a dead letter is not abandonment: the store enqueues a repair beside it, shown here as a
			<strong>resync</strong>, which retries slowly until the receiver comes back.
		</p>

		<div class="controlpanel flex flex-wrap items-center gap-3">
			<SelectField
				items={statusOptions}
				bind:value={statusFilter}
				on:change={() => fnLoad()}
				placeholder="Status"
				size="sm"
				class="w-44"
			/>
			<Button
				size="sm"
				color={statusFilter === 'dead' ? 'primary' : 'alternative'}
				on:click={() => {
					statusFilter = statusFilter === 'dead' ? '' : 'dead';
					fnLoad();
				}}
			>
				<i class="fa-solid fa-skull-crossbones mr-2" />Dead letters only
			</Button>
			<Button size="sm" color="alternative" on:click={() => fnLoad()}>
				<i class="fa-solid fa-arrows-rotate mr-2" />Refresh
			</Button>
		</div>

		{#if loading}
			<EmptyState variant="loading" title="Loading deliveries…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="Store-wide deliveries could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
			</EmptyState>
		{:else if deliveries.length === 0}
			<EmptyState
				title="No delivery matches."
				description="Nothing has been queued for these filters. An application with no webhook URL is pull-only and never appears here."
			/>
		{:else}
			<div class="relative overflow-x-auto">
				<Table>
					<TableHead>
						<TableHeadCell>Application</TableHeadCell>
						<TableHeadCell>Event</TableHeadCell>
						<TableHeadCell>Status</TableHeadCell>
						<TableHeadCell>Attempts</TableHeadCell>
						<TableHeadCell>Next attempt</TableHeadCell>
						<TableHeadCell>Last response</TableHeadCell>
						<TableHeadCell />
					</TableHead>
					<TableBody>
						{#each deliveries as delivery (delivery.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<div class="font-medium">{delivery.applicationName}</div>
									{#if delivery.userUUID}
										<div class="text-default text-xs font-mono break-all">{delivery.userUUID}</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<div>{delivery.eventType}</div>
									<div class="text-default text-xs">
										{delivery.entitlementKey ?? '—'}
										{#if delivery.version !== null}
											· v{delivery.version}
										{/if}
									</div>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Badge color={statusColors[delivery.status] ?? 'dark'}>{delivery.status}</Badge>
									{#if delivery.isResync}
										<Badge color="purple" class="ml-1">resync</Badge>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">{delivery.attemptCount}</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if delivery.deliveredAt}
										<RelativeDate date={delivery.deliveredAt} />
									{:else if delivery.nextAttemptAt}
										<RelativeDate date={delivery.nextAttemptAt} />
									{:else}
										<span class="text-default">—</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<div>{delivery.lastResponseStatus ?? '—'}</div>
									{#if delivery.lastError}
										<div class="text-default text-xs break-all">{delivery.lastError}</div>
									{/if}
									<!--
										What the receiver actually said. The status and the error between them
										cannot answer "why" — and on a success they do not even raise the
										question, which is the case this exists for.
									-->
									<WebhookResponseView {delivery} />
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if delivery.status !== 'delivered'}
										<Button
											size="xs"
											color="alternative"
											disabled={busyUUID === delivery.uuid}
											on:click={() => fnRetry(delivery)}
										>
											Retry delivery
										</Button>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			</div>

			{#if hasMore}
				<InfiniteLoadElement bind:this={sentinel} on:becomeVisible={fnLoadMore} />
			{/if}
		{/if}
	</div>
</Panel>
